/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle, BookOpen, FileCode, MessageSquare, Minus, Plus, Server, Settings2
} from 'lucide-react';
import MultiSelect from '../MultiSelect';
import {
  AI_AGENT_PROVIDERS, AI_AGENT_TYPES, findAiAgentProvider, getAiAgentModels, getAiAgentTypeLabel
} from '../../constants/aiAgents';
import { ACCOUNT_LABEL } from '../../constants/entityLabels';

// Mirror server/src/ai-agents/ai-agents.constants.ts
export const NAME_MAX_LENGTH = 200;
export const ROLE_MAX_LENGTH = 200;
export const PROMPT_MAX_LENGTH = 20000;
export const GREETING_MAX_LENGTH = 1000;
export const MCP_SERVERS_MAX = 50;
export const TEMPERATURE_MIN = 0;
export const TEMPERATURE_MAX = 1;
export const TEMPERATURE_STEP = 0.1;

const parseJsonArray = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    // Also accept Python-style single-quoted dicts, e.g. [{'type': 'mcp', ...}].
    return JSON.parse(value.replace(/'/g, '"'));
  }
};

/**
 * MCP servers textarea: a JSON array of server objects, sent upstream as-is,
 * e.g. [{ "type": "mcp", "server_label": "...", "server_url": "...", ... }].
 * Returns { servers } or { error }; servers is null when the box is empty.
 */
export const parseMcpServers = (text) => {
  const value = (text || '').trim().replace(/^`+|`+$/g, '').trim();
  if (!value) { return { servers: null }; }
  let servers;
  try {
    servers = parseJsonArray(value);
  } catch {
    return { error: 'Invalid JSON. Enter a JSON array of MCP server objects.' };
  }
  if (!Array.isArray(servers)) {
    return { error: 'MCP servers must be a JSON array.' };
  }
  if (!servers.every((s) => s && typeof s === 'object' && !Array.isArray(s))) {
    return { error: 'Each MCP server must be a JSON object.' };
  }
  if (servers.length > MCP_SERVERS_MAX) {
    return { error: `A maximum of ${MCP_SERVERS_MAX} MCP servers is allowed.` };
  }
  return { servers };
};

// Snap to the 0.1 grid (0.25 + 1 step -> 0.3, 0.25 - 1 step -> 0.2) within [MIN, MAX].
export const stepTemperature = (current, direction) => {
  const value = current === '' ? 0 : Number(current);
  const scaled = value / TEMPERATURE_STEP;
  const onGrid = Math.abs(scaled - Math.round(scaled)) < 1e-9;
  const nextScaled = onGrid
    ? Math.round(scaled) + direction
    : (direction > 0 ? Math.ceil(scaled) : Math.floor(scaled));
  const next = Math.round(nextScaled) / 10;
  return Math.min(TEMPERATURE_MAX, Math.max(TEMPERATURE_MIN, next));
};

/** Client-side validation shared by create and edit. Returns an errors object. */
export const validateAiAgentForm = (form, { validateMcp = false } = {}) => {
  const errors = {};
  if (!form.name.trim()) { errors.name = 'Name is required'; }
  if (!form.type) { errors.type = 'Type is required'; }
  if (!form.provider) { errors.provider = 'Provider is required'; }
  if (!form.model) { errors.model = 'Model is required'; }
  if (validateMcp && form.mcpServersDirty && parseMcpServers(form.mcpServersText).error) {
    errors.mcpServersText = parseMcpServers(form.mcpServersText).error;
  }
  // Edit only checks a changed temperature (an untouched legacy value is kept as-is).
  if ((form.temperatureDirty ?? true) && form.temperature !== '') {
    const t = Number(form.temperature);
    if (Number.isNaN(t) || t < TEMPERATURE_MIN || t > TEMPERATURE_MAX) {
      errors.temperature = `Temperature must be between ${TEMPERATURE_MIN} and ${TEMPERATURE_MAX}`;
    }
  }
  return errors;
};

const card = 'glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4';
const labelClass = 'text-[10px] font-black text-base-content/50 uppercase tracking-widest block';
const stepButtonClass = 'w-8 h-8 shrink-0 rounded-lg border border-base-content/10 text-base-content/70 hover:text-primary hover:border-primary/40 hover:bg-primary/5 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-all';

const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
    <Icon size={18} className="text-primary" />
    <h2 className="text-base font-black text-base-content uppercase tracking-wider">{title}</h2>
  </div>
);

SectionHeader.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
};

/**
 * The agent form cards (Configuration, Greeting, Prompt, MCP servers,
 * Knowledge bases) used by both the create and edit pages. The page owns the
 * `form` / `fieldErrors` state and the submit; this only renders and updates it.
 */
const AiAgentFormSections = ({
  form,
  setForm,
  fieldErrors,
  setFieldErrors,
  knowledgeBases,
  initial,
  showLive,
  showMcpServers,
  mcpServersHint,
  droppedKbCount,
  knowledgeBasesLoading,
  knowledgeBasesPlaceholder,
}) => {
  const setField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    setFieldErrors((p) => ({ ...p, [field]: null }));
  };

  const setTemperature = (value) => {
    setForm((f) => ({ ...f, temperature: value, temperatureDirty: true }));
    setFieldErrors((p) => ({ ...p, temperature: null }));
  };

  // Catalogue options, plus the agent's current value when it isn't listed (edit only),
  // so an existing agent can be saved without being forced onto another value.
  const typeOptions = useMemo(() => {
    const options = [...AI_AGENT_TYPES];
    if (initial.type && !options.some((t) => t.value === initial.type)) {
      options.push({ value: initial.type, label: `${getAiAgentTypeLabel(initial.type)} (current)` });
    }
    return options;
  }, [initial.type]);

  const providerOptions = useMemo(() => {
    const options = [...AI_AGENT_PROVIDERS];
    if (initial.provider && !findAiAgentProvider(initial.provider)) {
      options.push({ value: initial.provider, label: `${initial.provider} (current)`, models: [] });
    }
    return options;
  }, [initial.provider]);

  const modelOptions = useMemo(() => {
    if (!form.provider) { return []; }
    const options = [...getAiAgentModels(form.provider)];
    if (form.provider === initial.provider && initial.model
      && !options.some((m) => m.value === initial.model)) {
      options.push({ value: initial.model, label: `${initial.model} (current)` });
    }
    return options;
  }, [form.provider, initial.provider, initial.model]);

  const selectedModel = modelOptions.find((m) => m.value === form.model);

  const kbOptions = useMemo(
    () => knowledgeBases.map((kb) => ({
      id: String(kb.id), key: kb.name || String(kb.id), description: kb.description || undefined,
    })),
    [knowledgeBases]
  );

  const temperatureValue = form.temperature !== ''
    ? Math.min(TEMPERATURE_MAX, Math.max(TEMPERATURE_MIN, Number(form.temperature)))
    : TEMPERATURE_MIN;

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50 ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;
  const errorText = (field) => fieldErrors[field] && <p className="text-error text-xs font-medium">{fieldErrors[field]}</p>;

  const renderKnowledgeBases = () => {
    if (knowledgeBasesPlaceholder) {
      return <p className="text-xs text-base-content/40">{knowledgeBasesPlaceholder}</p>;
    }
    if (knowledgeBasesLoading) {
      return <div className="h-11 bg-base-200/50 rounded-xl animate-pulse"></div>;
    }
    if (knowledgeBases.length === 0) {
      return (
        <p className="text-xs text-base-content/40">
          This {ACCOUNT_LABEL.singularLower} has no knowledge bases yet. Create one under Knowledge Bases to link it here.
        </p>
      );
    }
    return (
      <MultiSelect
        label="Linked Knowledge Bases"
        searchPlaceholder="Search knowledge bases..."
        placeholder="Select knowledge bases (optional)"
        options={kbOptions}
        selected={form.knowledgeBaseIds}
        onChange={(ids) => setField('knowledgeBaseIds', ids)}
      />
    );
  };

  return (
    <>
      {/* Configuration */}
      <div className={card}>
        <SectionHeader icon={Settings2} title="Configuration" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label htmlFor="name" className={labelClass}>Name *</label>
            <input id="name" type="text" maxLength={NAME_MAX_LENGTH} placeholder="e.g. Plans identifier"
              className={inputClass('name')} value={form.name} onChange={(e) => setField('name', e.target.value)} />
            {errorText('name')}
          </div>
          <div className="space-y-1.5">
            <label htmlFor="role" className={labelClass}>Role</label>
            <input id="role" type="text" maxLength={ROLE_MAX_LENGTH} placeholder="e.g. Assistant"
              className={inputClass('role')} value={form.role} onChange={(e) => setField('role', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="provider" className={labelClass}>Provider *</label>
            <select
              id="provider"
              className={inputClass('provider')}
              value={form.provider}
              onChange={(e) => {
                const provider = e.target.value;
                // Models depend on the provider — pick again after switching.
                setForm((f) => ({ ...f, provider, model: '' }));
                setFieldErrors((p) => ({ ...p, provider: null, model: null }));
              }}
            >
              <option value="">— Select a provider —</option>
              {providerOptions.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            {errorText('provider')}
          </div>
          <div className="space-y-1.5">
            <label htmlFor="model" className={labelClass}>Model *</label>
            <select
              id="model"
              className={`${inputClass('model')} font-mono`}
              value={form.model}
              disabled={!form.provider}
              onChange={(e) => setField('model', e.target.value)}
            >
              <option value="">{form.provider ? '— Select a model —' : '— Select a provider first —'}</option>
              {modelOptions.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            {selectedModel?.description && (
              <p className="text-xs text-base-content/50">{selectedModel.description}</p>
            )}
            {errorText('model')}
          </div>
          <div className="space-y-1.5">
            <label htmlFor="temperature" className={labelClass}>Temperature</label>
            <div className="flex items-center gap-3 px-3 py-2 bg-base-100 border border-base-300 rounded-xl">
              <button
                type="button"
                onClick={() => setTemperature(stepTemperature(form.temperature, -1))}
                disabled={temperatureValue <= TEMPERATURE_MIN}
                className={stepButtonClass}
                aria-label="Decrease temperature"
              >
                <Minus size={14} />
              </button>
              <input
                id="temperature"
                type="range"
                min={TEMPERATURE_MIN}
                max={TEMPERATURE_MAX}
                step={TEMPERATURE_STEP}
                value={temperatureValue}
                onChange={(e) => setTemperature(Number(e.target.value))}
                className="range range-primary range-xs flex-1"
              />
              <button
                type="button"
                onClick={() => setTemperature(stepTemperature(form.temperature, 1))}
                disabled={temperatureValue >= TEMPERATURE_MAX}
                className={stepButtonClass}
                aria-label="Increase temperature"
              >
                <Plus size={14} />
              </button>
              <span className="w-10 shrink-0 text-right font-mono text-sm font-bold text-base-content" aria-live="polite">
                {form.temperature === '' ? '—' : Number(form.temperature).toString()}
              </span>
            </div>
            {errorText('temperature')}
            <p className="text-base-content/40 text-[10px] font-medium">
              {TEMPERATURE_MIN}–{TEMPERATURE_MAX} in steps of {TEMPERATURE_STEP}. Lower is more focused, higher is more creative.
            </p>
          </div>
          <div className="space-y-1.5">
            <label htmlFor="type" className={labelClass}>Type *</label>
            <select
              id="type"
              className={inputClass('type')}
              value={form.type}
              onChange={(e) => setField('type', e.target.value)}
            >
              <option value="">— Select a type —</option>
              {typeOptions.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
            {errorText('type')}
          </div>
        </div>

        {showLive && (
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-base-200/50 border border-base-content/5">
            <div>
              <p className="text-sm font-bold text-base-content">Live</p>
              <p className="text-xs text-base-content/50">When off, the agent is kept but not available to users.</p>
            </div>
            <label aria-label="Agent live toggle" className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={form.isLive}
                onChange={(e) => setField('isLive', e.target.checked)}
              />
              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary shadow-inner"></div>
            </label>
          </div>
        )}
      </div>

      {/* Greeting message */}
      <div className={card}>
        <SectionHeader icon={MessageSquare} title="Greeting Message" />
        <textarea id="greetingMessage" rows={3} maxLength={GREETING_MAX_LENGTH}
          placeholder="e.g. Hi there! I'm your AI assistant. How can I help you today?"
          className={`${inputClass('greetingMessage')} resize-y`} value={form.greetingMessage}
          onChange={(e) => setField('greetingMessage', e.target.value)} />
        <p className="text-base-content/40 text-[10px] font-medium text-right">
          {form.greetingMessage.length}/{GREETING_MAX_LENGTH}
        </p>
      </div>

      {/* Prompt */}
      <div className={card}>
        <SectionHeader icon={FileCode} title="Prompt" />
        <textarea id="prompt" rows={8} maxLength={PROMPT_MAX_LENGTH}
          placeholder="e.g. You are an expert technician. Answer questions strictly using the assigned knowledge bases."
          className={`${inputClass('prompt')} resize-y font-mono text-xs`} value={form.prompt}
          onChange={(e) => setField('prompt', e.target.value)} />
        <p className="text-base-content/40 text-[10px] font-medium text-right">
          {form.prompt.length}/{PROMPT_MAX_LENGTH}
        </p>
      </div>

      {/* MCP servers (free-form for now) */}
      {showMcpServers && (
        <div className={card}>
          <SectionHeader icon={Server} title="MCP Servers" />
          <textarea
            id="mcpServers"
            rows={10}
            placeholder={'[{ "type": "mcp", "server_label": "servicehub-mcp", "server_url": "https://.../mcp", "authorization": "<token>", "require_approval": "never" }]'}
            className={`${inputClass('mcpServersText')} resize-y font-mono text-xs`}
            value={form.mcpServersText}
            onChange={(e) => setForm((f) => ({ ...f, mcpServersText: e.target.value, mcpServersDirty: true }))}
            onBlur={() => {
              if (form.mcpServersDirty) {
                const { error } = parseMcpServers(form.mcpServersText);
                setFieldErrors((p) => ({ ...p, mcpServersText: error || null }));
              }
            }}
          />
          {errorText('mcpServersText')}
          <p className="text-base-content/40 text-[10px] font-medium">
            {mcpServersHint}
          </p>
        </div>
      )}

      {/* Knowledge bases (from the agent account's knowledge base list) */}
      <div className={`${card} relative z-20`}>
        <SectionHeader icon={BookOpen} title="Knowledge Bases" />
        {droppedKbCount > 0 && (
          <div role="alert" className="alert alert-warning rounded-xl text-xs font-semibold">
            <AlertTriangle size={16} className="shrink-0" />
            <span>
              {droppedKbCount} linked knowledge base{droppedKbCount === 1 ? ' no longer exists' : 's no longer exist'} for
              this {ACCOUNT_LABEL.singularLower} and will be unlinked when you save.
            </span>
          </div>
        )}
        {renderKnowledgeBases()}
      </div>
    </>
  );
};

AiAgentFormSections.propTypes = {
  form: PropTypes.shape({
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    provider: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    temperature: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    greetingMessage: PropTypes.string.isRequired,
    prompt: PropTypes.string.isRequired,
    knowledgeBaseIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    isLive: PropTypes.bool,
    mcpServersText: PropTypes.string,
    mcpServersDirty: PropTypes.bool,
  }).isRequired,
  setForm: PropTypes.func.isRequired,
  fieldErrors: PropTypes.object.isRequired,
  setFieldErrors: PropTypes.func.isRequired,
  knowledgeBases: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
  })).isRequired,
  // The agent's original type/provider/model (edit); empty strings for create.
  initial: PropTypes.shape({
    type: PropTypes.string,
    provider: PropTypes.string,
    model: PropTypes.string,
  }),
  showLive: PropTypes.bool,
  showMcpServers: PropTypes.bool,
  // Help text under the MCP servers box (defaults to the edit-page wording).
  mcpServersHint: PropTypes.string,
  droppedKbCount: PropTypes.number,
  knowledgeBasesLoading: PropTypes.bool,
  // When set, shown instead of the knowledge base picker (e.g. "Select an account first").
  knowledgeBasesPlaceholder: PropTypes.string,
};

AiAgentFormSections.defaultProps = {
  initial: { type: '', provider: '', model: '' },
  showLive: false,
  showMcpServers: false,
  mcpServersHint: 'A JSON array of MCP server objects, sent as-is. Leave it untouched to keep the current MCP servers; clear it to remove them all.',
  droppedKbCount: 0,
  knowledgeBasesLoading: false,
  knowledgeBasesPlaceholder: '',
};

export default AiAgentFormSections;
